import React, { useState, useCallback, useMemo } from "react";
import PageTitle from "../../../components/Common/PageTitle/index";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import PaymentModal from "../../Content/PaymentModal/PaymentModal";
import AppointmentDetailsModal from "../AppointmentDetails/AppointmentDetailsModal";
import AgreementSigningModal from "../../../components/Modals/AgreementSigningModal";
import TelehealthConsentModal from "../../../components/Modals/TelehealthConsentModal";
import ComposeMessageModal from "../../../components/Modals/ComposeMessageModal";
import {
  ConfirmationSnackbar,
} from "../../../components/Common/CustomSnackbar";
import GHLCalendarModal from "./components/GHLCalendarModal";
import { colors } from "../../../theme/theme";
import axiosInstance from "../../../utils/config/config";

import {
  PageWrapper,
  ContentBox,
  LoadingBox,
  ErrorAlert,
} from "./Patient.styles";
import {
  BOOKING_URL,
  DEFAULT_HOURLY_RATE,
  EMPTY_STATE_TYPES,
  AGREEMENT_BANNER,
  TELEHEALTH_BANNER,
} from "./Patient.constants";
import {
  CANCEL_CONFIRM,
  CONSULTATION_REQUIRED_TOOLTIP,
  AGREEMENT_REQUIRED_TOOLTIP,
  TELEHEALTH_CONSENT_REQUIRED_TOOLTIP,
} from "./components/AppointmentsTable/AppointmentsTable.constants";
import {
  isFirstAppointmentForPatient,
  isAppointmentPaid,
  isComplimentary,
  needsPayment,
  hasCompletedConsultation,
} from "./Patient.utils";
import { usePatientAppointments } from "./Patient.hooks";
import AppointmentsTable from "./components/AppointmentsTable";
import PatientEmptyState from "./components/PatientEmptyState";

const getValidHourlyRate = (rate, fallback = DEFAULT_HOURLY_RATE) => {
  const parsed = parseFloat(rate);
  return parsed > 0 && parsed <= 10000 ? parsed : fallback;
};

const PatientPage = () => {
  const {
    appointments: upcomingAppointments,
    patient,
    iphcPractitioner,
    payments,
    isPractitioner,
    isLoading,
    paymentModal,
    processingPayment,
    paymentError,
    setPaymentError,
    appointmentDetailsModal,
    getPractitionerNameFn,
    handleConfirmCharge,
    handleClosePaymentModal,
    handleOpenChargeModal,
    handleBookFollowUp,
    handleViewAppointmentDetails,
    handleCloseAppointmentDetails,
    handleRequestCancel,
    handleDismissCancel,
    handleConfirmCancel,
    handleReschedule,
    handleConfirmReschedule,
    handleDismissReschedule,
    handleCloseCalendarModal,
    handleCalendarBookingDetected,
    cancelConfirm,
    rescheduleConfirm,
    calendarModal,
    agreementState,
    clientAgreementStatuses,
    handleOpenAgreementModal,
    handleCloseAgreementModal,
    handleAgreementSigned,
    pastAppointments,
    pastAppointmentsLoading,
    telehealthConsentState,
    clientTelehealthConsentStatuses,
    handleOpenTelehealthConsentModal,
    handleCloseTelehealthConsentModal,
    handleTelehealthConsentSigned,
  } = usePatientAppointments();

  // Message compose state
  const [composeModal, setComposeModal] = useState({
    open: false,
    contact: null,
  });
  const [messageSending, setMessageSending] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);
  const [isUpcomingSession, setIsUpcomingSession] = useState(true);

  const appointments = isUpcomingSession
    ? upcomingAppointments
    : pastAppointments;

  const handleMessageClient = useCallback((appointment) => {
    const contactName = appointment?.contact?.name || appointment?.title || "";
    const contactEmail = appointment?.contact?.email || "";
    // Try to resolve the patient's userId for the messaging API
    // We use the contact email to find or construct the contact object
    if (contactEmail) {
      setComposeModal({
        open: true,
        contact: {
          id: appointment?.contact?.userId || null,
          firstName: contactName.split(" ")[0] || "",
          lastName: contactName.split(" ").slice(1).join(" ") || "",
          email: contactEmail,
        },
      });
    } else {
      setPaymentError(
        "No email address found for this client. They may not have completed their intake form.",
      );
    }
  }, [setPaymentError]);

  const handleSendMessage = useCallback(async ({ toUserId, subject, body }) => {
    setMessageSending(true);
    try {
      // If we don't have a userId, look it up by email
      let recipientId = toUserId;
      if (!recipientId && composeModal.contact?.email) {
        const res = await axiosInstance.get(
          `user/find-by-email?email=${encodeURIComponent(composeModal.contact.email)}`,
        );
        recipientId = res.data?.user?.id;
      }
      if (!recipientId) {
        throw new Error("Could not find recipient. They may not have an account yet.");
      }
      const response = await axiosInstance.post("messages/send", {
        toUserId: recipientId,
        subject,
        body,
      });
      if (response.data.success) {
        setMessageSuccess(true);
        setComposeModal({ open: false, contact: null });
      } else {
        throw new Error(response.data.message || "Failed to send message.");
      }
    } catch (err) {
      throw new Error(
        err.response?.data?.message || err.message || "Failed to send message.",
      );
    } finally {
      setMessageSending(false);
    }
  }, [composeModal.contact?.email]);

  const openBooking = () => window.open(BOOKING_URL, "_blank");

  // For clients: follow-up scheduling is gated behind a completed consultation
  const consultationCompleted = useMemo(() => {
    if (isPractitioner) return true;
    return hasCompletedConsultation([
      ...upcomingAppointments,
      ...pastAppointments,
    ]);
  }, [isPractitioner, upcomingAppointments, pastAppointments]);

  // For clients: booking is also gated behind a signed agreement
  const agreementSigned = isPractitioner ? true : agreementState.signed;
  // ...and a signed Telehealth Informed Consent.
  const telehealthConsentSigned = isPractitioner
    ? true
    : telehealthConsentState.signed;
  const canBookFollowUp =
    consultationCompleted && agreementSigned && telehealthConsentSigned;

  const renderContent = () => {
    if (isLoading || (pastAppointmentsLoading && !isUpcomingSession)) {
      return (
        <LoadingBox>
          <CircularProgress size={50} sx={{ color: colors.primary }} />
        </LoadingBox>
      );
    }

    if (isPractitioner) {
      return appointments.length > 0 ? (
        <AppointmentsTable
          variant="practitioner"
          appointments={appointments}
          getPractitionerName={getPractitionerNameFn}
          onViewDetails={handleViewAppointmentDetails}
          onBookFollowUp={handleBookFollowUp}
          onCancel={handleRequestCancel}
          onReschedule={handleReschedule}
          onMessageClient={handleMessageClient}
          getIsPaid={(appt) => isAppointmentPaid(appt.id, payments)}
          getIsFreeConsultation={(appt) =>
            isComplimentary(appointments, appt, patient)
          }
          isUpcomingSession={isUpcomingSession}
        />
      ) : (
        <PatientEmptyState
          type={
            isUpcomingSession
              ? EMPTY_STATE_TYPES.PRACTITIONER_NO_APPOINTMENTS
              : EMPTY_STATE_TYPES.PRACTITIONER_NO_PAST_APPOINTMENTS
          }
        />
      );
    }

    if (!patient) {
      return (
        <PatientEmptyState
          type={EMPTY_STATE_TYPES.NOT_REGISTERED}
          onBookClick={openBooking}
        />
      );
    }

    if (appointments.length > 0) {
      return (
        <>
          {!agreementSigned && !agreementState.loading && (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: "14px",
                border: `2px solid ${colors.accent}40`,
                backgroundColor: `${colors.accent}08`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <DescriptionIcon sx={{ color: colors.accent, fontSize: 28 }} />
                <Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontWeight: 700,
                      color: colors.primary,
                      fontSize: "0.95rem",
                      display: "block",
                    }}
                  >
                    {AGREEMENT_BANNER.TITLE}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontWeight: 400,
                      color: colors.textLight,
                      fontSize: "0.85rem",
                    }}
                  >
                    {AGREEMENT_BANNER.SUBTITLE}
                  </Box>
                </Box>
              </Box>
              <CustomBtn
                text={AGREEMENT_BANNER.BUTTON_TEXT}
                noIcon
                onClick={handleOpenAgreementModal}
              />
            </Box>
          )}
          {!telehealthConsentSigned && !telehealthConsentState.loading && (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: "14px",
                border: `2px solid ${colors.accent}40`,
                backgroundColor: `${colors.accent}08`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <DescriptionIcon sx={{ color: colors.accent, fontSize: 28 }} />
                <Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontWeight: 700,
                      color: colors.primary,
                      fontSize: "0.95rem",
                      display: "block",
                    }}
                  >
                    {TELEHEALTH_BANNER.TITLE}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "'Gotham', sans-serif",
                      fontWeight: 400,
                      color: colors.textLight,
                      fontSize: "0.85rem",
                    }}
                  >
                    {TELEHEALTH_BANNER.SUBTITLE}
                  </Box>
                </Box>
              </Box>
              <CustomBtn
                text={TELEHEALTH_BANNER.BUTTON_TEXT}
                noIcon
                onClick={handleOpenTelehealthConsentModal}
              />
            </Box>
          )}
          <AppointmentsTable
            variant="patient"
            isUpcomingSession={isUpcomingSession}
            appointments={appointments}
            getPractitionerName={getPractitionerNameFn}
            onViewDetails={handleViewAppointmentDetails}
            onBookFollowUp={handleBookFollowUp}
            onCancel={handleRequestCancel}
            onReschedule={handleReschedule}
            consultationCompleted={canBookFollowUp}
          />
          <Box sx={{ mt: 3 }}>
            {canBookFollowUp ? (
              <CustomBtn
                text="Book Another Session"
                noIcon
                onClick={openBooking}
              />
            ) : !consultationCompleted ? (
              <Tooltip title={CONSULTATION_REQUIRED_TOOLTIP} arrow>
                <span>
                  <CustomBtn
                    text="Book Another Session"
                    noIcon
                    onClick={() => {}}
                    disabled
                  />
                </span>
              </Tooltip>
            ) : !agreementSigned ? (
              <Tooltip title={AGREEMENT_REQUIRED_TOOLTIP} arrow>
                <span>
                  <CustomBtn
                    text="Book Another Session"
                    noIcon
                    onClick={() => {}}
                    disabled
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={TELEHEALTH_CONSENT_REQUIRED_TOOLTIP} arrow>
                <span>
                  <CustomBtn
                    text="Book Another Session"
                    noIcon
                    onClick={() => {}}
                    disabled
                  />
                </span>
              </Tooltip>
            )}
          </Box>
        </>
      );
    }

    return (
      <PatientEmptyState
        type={EMPTY_STATE_TYPES.PATIENT_NO_APPOINTMENTS}
        onBookClick={openBooking}
      />
    );
  };

  const modalAppointment = appointmentDetailsModal.appointment;

  return (
    <PageWrapper>
      <PageTitle
        noIcon
        text={
          isPractitioner
            ? "Nirva Practitioner Sessions Dashboard"
            : "Nirva Client Sessions Dashboard"
        }
        isUpcomingSession={isUpcomingSession}
        textAsLink
        setIsUpcomingSession={setIsUpcomingSession}
        title={isPractitioner ? "My Sessions" : "Nirva Client Sessions"}
      />

      {paymentError && (
        <ErrorAlert severity="error" onClose={() => setPaymentError(null)}>
          {paymentError}
        </ErrorAlert>
      )}

      <GHLCalendarModal
        open={calendarModal.open}
        onClose={handleCloseCalendarModal}
        calendarUrl={calendarModal.calendarUrl}
        title={calendarModal.title}
        onBookingDetected={handleCalendarBookingDetected}
      />

      <ContentBox>{renderContent()}</ContentBox>

      <AppointmentDetailsModal
        open={appointmentDetailsModal.open}
        onClose={handleCloseAppointmentDetails}
        appointment={modalAppointment}
        practitionerName={
          modalAppointment
            ? getPractitionerNameFn(modalAppointment.assignedUserId)
            : ""
        }
        isPaid={
          modalAppointment
            ? isAppointmentPaid(modalAppointment.id, payments)
            : false
        }
        isFreeConsultation={
          modalAppointment
            ? isPractitioner
              ? isComplimentary(appointments, modalAppointment, patient)
              : isFirstAppointmentForPatient(appointments, modalAppointment)
            : false
        }
        requiresPayment={
          modalAppointment
            ? isPractitioner
              ? needsPayment(appointments, modalAppointment, payments, patient)
              : !isAppointmentPaid(modalAppointment.id, payments) &&
                !isFirstAppointmentForPatient(appointments, modalAppointment)
            : false
        }
        onBookFollowUp={handleBookFollowUp}
        onChargePatient={isPractitioner ? handleOpenChargeModal : undefined}
        onCancel={handleRequestCancel}
        onReschedule={handleReschedule}
        isPractitioner={isPractitioner}
        consultationCompleted={consultationCompleted}
        agreementSigned={
          isPractitioner
            ? modalAppointment?.contact?.email
              ? clientAgreementStatuses[modalAppointment.contact.email]
              : undefined
            : agreementState.signed
        }
        telehealthConsentSigned={
          isPractitioner
            ? modalAppointment?.contact?.email
              ? clientTelehealthConsentStatuses[modalAppointment.contact.email]
              : undefined
            : telehealthConsentState.signed
        }
        isUpcomingSession={isUpcomingSession}
      />

      <PaymentModal
        open={paymentModal.open}
        onClose={handleClosePaymentModal}
        onConfirm={handleConfirmCharge}
        appointment={paymentModal.appointment}
        cardInfo={paymentModal.cardInfo}
        practitionerName={
          paymentModal.appointment
            ? getPractitionerNameFn(paymentModal.appointment.assignedUserId)
            : ""
        }
        processing={processingPayment}
        amount={
          paymentModal.calculatedAmount ||
          getValidHourlyRate(iphcPractitioner?.hourlyRate)
        }
        appointmentDuration={paymentModal.appointmentDuration}
        hourlyRate={getValidHourlyRate(iphcPractitioner?.hourlyRate)}
        paymentSuccess={paymentModal.success}
        paymentData={paymentModal.paymentData}
      />
      <Dialog open={cancelConfirm.open} onClose={handleDismissCancel}>
        <DialogTitle>{CANCEL_CONFIRM.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{CANCEL_CONFIRM.MESSAGE}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismissCancel}>{CANCEL_CONFIRM.DISMISS}</Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            {CANCEL_CONFIRM.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={rescheduleConfirm.open} onClose={handleDismissReschedule}>
        <DialogTitle>Confirm Reschedule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Did you successfully book a new session? The original appointment
            will be cancelled and marked as rescheduled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismissReschedule}>
            No, Keep Original
          </Button>
          <Button onClick={handleConfirmReschedule} color="primary" variant="contained">
            Yes, Cancel Original
          </Button>
        </DialogActions>
      </Dialog>

      <AgreementSigningModal
        open={agreementState.modalOpen}
        onClose={handleCloseAgreementModal}
        onSigned={handleAgreementSigned}
        isMinor={agreementState.isMinor}
      />

      <TelehealthConsentModal
        open={telehealthConsentState.modalOpen}
        onClose={handleCloseTelehealthConsentModal}
        onSigned={handleTelehealthConsentSigned}
        isMinor={telehealthConsentState.isMinor}
      />

      {/* Message compose modal for practitioners */}
      {isPractitioner && (
        <ComposeMessageModal
          open={composeModal.open}
          handleClose={() => setComposeModal({ open: false, contact: null })}
          contacts={composeModal.contact ? [composeModal.contact] : []}
          preSelectedContact={composeModal.contact}
          onSend={handleSendMessage}
          sending={messageSending}
        />
      )}
      <ConfirmationSnackbar
        snackbarState={messageSuccess}
        handleSnackbarClose={() => setMessageSuccess(false)}
        text="Message sent successfully!"
      />
    </PageWrapper>
  );
};

export default PatientPage;
